import {useMutation} from '@apollo/client'
import {parseCookies} from 'nookies'
import {useCallback, useMemo} from 'react'
import {SET_SHIPPING_METHODS_ON_CART} from '_app/graphql/mutations/cart/SetShippingMethodsOnCart'
import useCart from '_app/hooks/useCart'
import {CartAddressInput, useSetShippingAddressOnCartMutation,} from '_app/types/api'
import logger from '_app/utils/logger'
import {COURIER_NOT_ALLOWED_METHODS, PICKUP_ALLOWED_METHODS, PICKUP_NOT_ALLOWED_METHODS,} from './constants'
import {ShippingMethods} from './types'
import {prepareShippingAddress} from './utils'

export const useShippingMethod = () => {
    const {data} = useCart()
    const {cartId} = parseCookies()
    const [mutation] = useMutation(SET_SHIPPING_METHODS_ON_CART, {
        refetchQueries: ['GetCart', 'GetCartPrices'],
    })
    const [addressMutation] = useSetShippingAddressOnCartMutation()

    const methodsInCart = useMemo(
        () =>
            (
                data?.cart?.shipping_addresses?.[0]?.available_shipping_methods || []
            ).filter(
                method =>
                    !!method &&
                    !COURIER_NOT_ALLOWED_METHODS.includes(method?.carrier_code) &&
                    !PICKUP_NOT_ALLOWED_METHODS.includes(method?.carrier_code)
            ),
        [data]
    )
    const shippingMethods = useMemo(() => {
        return methodsInCart.reduce(
            (acc, method) => {
                if (!method) return acc

                const type = PICKUP_ALLOWED_METHODS.includes(
                    method.carrier_code as typeof PICKUP_ALLOWED_METHODS[number]
                )
                    ? 'pickup'
                    : 'courier'
                acc[type].push(method)

                return acc
            },
            {pickup: [], courier: []} as ShippingMethods
        )
    }, [methodsInCart])
    const setShippingMethodsOnCart = useCallback(
        async ({
                   delivery_option,
                   inpost_point,
                   showroom,
               }: {
            delivery_option: string;
            inpost_point?: string;
            showroom?: string;
        }) => {
            try {
                await mutation({
                    variables: {
                        shippingMethods: [
                            {
                                carrier_code: delivery_option,
                                method_code:
                                    methodsInCart.find(
                                        method => method?.carrier_code === delivery_option
                                    )?.method_code || '',
                                showroom_target: showroom,
                                paczkomaty_target_point: inpost_point,
                            },
                        ],
                    },
                })
            } catch (e) {
                logger.error(e)
            }
        },
        [methodsInCart, mutation]
    )

    const setShippingAddressOnCart = useCallback(
        (address: Partial<CartAddressInput>) => {
            const fullAddress = prepareShippingAddress(data, address)

            addressMutation({
                variables: {
                    cartId,
                    shippingAddresses: [
                        {
                            address: fullAddress,
                        },
                    ],
                },
            })
        },
        [data, addressMutation, cartId]
    )

    return {
        shippingMethods,
        setShippingMethodsOnCart,
        setShippingAddressOnCart,
    }
}
